// Small dialogs the records screens share: a reason (holds, reopening,
// withdrawing, cancelling an act) and a later keep-until date.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { FieldError, REQUIRED_LABEL } from '@/shared/ui/FieldError.jsx';
import { records } from '../lib/api.js';
import { fmtDay, todayInTashkent } from '../lib/journals.js';
import { LIMITS, extendProblems, nextDay, reasonProblem } from '../lib/validation.js';

// The reason box every "undo or hold" dialog asks for: required, at least
// three characters, at most 2000 (the server's Reason).
export function ReasonInput({ id, value, onChange, error }) {
  const { t } = useTranslation('records');
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className={REQUIRED_LABEL}>{t('entry.reason')}</Label>
      <Input
        id={id}
        required
        minLength={LIMITS.reasonMin}
        maxLength={LIMITS.reason}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

export function ReasonDialog({ open, title, description, confirmLabel, onOpenChange, onConfirm }) {
  const { t } = useTranslation('records');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const problem = reasonProblem(reason);
  const checks = useFieldErrors(problem ? { reason: problem } : {});
  const { reset } = checks;
  useEffect(() => {
    if (open) {
      setReason('');
      reset();
    }
  }, [open, reset]);
  const submit = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <ReasonInput
          id="case-reason"
          value={reason}
          onChange={(v) => { setReason(v); checks.touch('reason'); }}
          error={checks.errors.reason}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
          <Button onClick={submit} disabled={busy || !reason.trim() || checks.blocked} data-testid="reason-confirm">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ExtendDialog({ target, onOpenChange, onDone }) {
  const { t } = useTranslation('records');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const today = todayInTashkent();
  // Later than today and than the date the case has now.
  const current = target?.retention_until ? String(target.retention_until).slice(0, 10) : null;
  const floor = current && current > today ? current : today;
  const checks = useFieldErrors(extendProblems({ until, reason }, { today, current }));
  const { reset } = checks;
  useEffect(() => {
    setUntil('');
    setReason('');
    reset();
  }, [target, reset]);
  const submit = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    const res = await records.extend(target.uuid, until, reason.trim());
    setBusy(false);
    if (res.success) {
      toast.success(t('review.extended', { index: target.case_index, date: fmtDay(until) }));
      onDone?.();
      onOpenChange(false);
    } else {
      checks.showServer(res, ['until', 'reason']);
    }
  };
  return (
    <Dialog open={Boolean(target)} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('review.extendTitle', { index: target?.case_index })}</DialogTitle>
          <DialogDescription>{t('review.extendDescription')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="extend-until" className={REQUIRED_LABEL}>{t('review.until')}</Label>
            <Input id="extend-until" type="date" required min={nextDay(floor)} max={LIMITS.dateMax} value={until}
              onChange={(e) => { setUntil(e.target.value); checks.touch('until'); }} {...checks.describe('until', 'extend')} />
            <FieldError id="extend-until-error" message={checks.errors.until} />
          </div>
          <ReasonInput
            id="extend-reason"
            value={reason}
            onChange={(v) => { setReason(v); checks.touch('reason'); }}
            error={checks.errors.reason}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
          <Button onClick={submit} disabled={busy || !until || !reason.trim() || checks.blocked} data-testid="extend-confirm">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('review.extend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
